import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  castVote,
  getMyVotes,
  getResults,
  loadQuestions,
} from "../lib/api";
import type { Question, QuestionResult, UserVotes } from "../lib/types";
import { useAuth } from "../lib/useAuth";

export default function QuestionPage() {
  const { questionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [questions, setQuestions] = useState<Question[]>([]);
  const [votes, setVotes] = useState<UserVotes>({});
  const [result, setResult] = useState<QuestionResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void loadQuestions().then((d) => setQuestions(d.questions));
  }, []);

  useEffect(() => {
    if (user) void getMyVotes().then(setVotes);
    if (questionId) {
      void getResults({ questionId }).then((r) => setResult(r.results[questionId] ?? null));
    }
  }, [user, questionId]);

  const question = useMemo(
    () => questions.find((q) => q.id === questionId) ?? null,
    [questions, questionId]
  );

  if (!questionId || !question) {
    return <p className="text-muted">Loading…</p>;
  }

  const myVote = votes[questionId];
  const prev = questions.find((q) => q.number === question.number - 1);
  const next = questions.find((q) => q.number === question.number + 1);

  async function handleVote(optionKey: string) {
    if (!user) {
      const redirect = encodeURIComponent(`/questions/${questionId}`);
      navigate(`/register?next=${redirect}`);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await castVote(questionId!, optionKey);
      const updated = { ...votes, [questionId!]: { optionKey, updatedAt: Date.now() / 1000 } };
      setVotes(updated);
      const r = await getResults({ questionId: questionId! });
      setResult(r.results[questionId!] ?? null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className="space-y-8">
      <div>
        <Link to={`/themes/${question.themeSlug}`} className="text-sm">
          ← Theme {question.themeId}
        </Link>
        <div className="mt-2 flex items-baseline gap-3">
          <span className="text-xs uppercase tracking-wider text-muted">
            {question.id}
          </span>
          <span className="text-xs text-muted">Question {question.number} of 100</span>
        </div>
        <h1 className="text-2xl sm:text-3xl font-semibold leading-tight mt-2 max-w-prose">
          {question.title}
        </h1>
      </div>

      <section className="max-w-prose">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted mb-2">
          Context
        </h2>
        <p className="text-ink/90 leading-relaxed">{question.context}</p>
      </section>

      <section className="max-w-prose">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted mb-3">
          Your vote
        </h2>
        <ul className="space-y-2">
          {question.options.map((opt) => {
            const isMine = myVote?.optionKey === opt.key;
            return (
              <li key={opt.key}>
                <button
                  onClick={() => handleVote(opt.key)}
                  disabled={busy}
                  className={`w-full text-left card transition ${
                    isMine
                      ? "border-cedar ring-1 ring-cedar"
                      : "hover:border-cedar"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <span className="font-mono text-sm bg-sand rounded px-1.5 py-0.5 mt-0.5">
                      {opt.key}
                    </span>
                    <span className="flex-1">
                      <span>{opt.label}</span>
                      {isMine && (
                        <span className="ml-2 text-xs text-rust">your vote</span>
                      )}
                    </span>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
        {error && <p className="text-sm text-rust mt-3">Error: {error}</p>}
        {!user && (
          <p className="text-sm text-muted mt-3">
            <Link to={`/register?next=${encodeURIComponent(`/questions/${questionId}`)}`}>
              Register
            </Link>{" "}
            to record your vote. Voting is anonymous.
          </p>
        )}
      </section>

      <ResultsBar result={result} options={question.options} />

      <nav className="flex items-center justify-between pt-4 border-t border-ink/10">
        {prev ? (
          <Link to={`/questions/${prev.id}`} className="text-sm">
            ← {prev.id}
          </Link>
        ) : (
          <span />
        )}
        {next ? (
          <Link to={`/questions/${next.id}`} className="text-sm">
            {next.id} →
          </Link>
        ) : (
          <span />
        )}
      </nav>
    </article>
  );
}

function ResultsBar({
  result,
  options,
}: {
  result: QuestionResult | null;
  options: Question["options"];
}) {
  if (!result || result.total === 0) {
    return (
      <section className="max-w-prose">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted mb-2">
          Current results
        </h2>
        <p className="text-sm text-muted">No votes yet. Be the first.</p>
      </section>
    );
  }

  return (
    <section className="max-w-prose">
      <h2 className="text-sm font-semibold uppercase tracking-wider text-muted mb-3">
        Current results · {result.total} {result.total === 1 ? "vote" : "votes"}
      </h2>
      <ul className="space-y-2">
        {options.map((opt) => {
          const n = result.options[opt.key] ?? 0;
          const pct = Math.round((n / result.total) * 100);
          return (
            <li key={opt.key}>
              <div className="flex items-baseline gap-3 mb-1">
                <span className="font-mono text-xs bg-sand rounded px-1 py-0.5">
                  {opt.key}
                </span>
                <span className="text-sm flex-1 truncate">{opt.label}</span>
                <span className="text-sm text-muted tabular-nums">
                  {pct}% · {n}
                </span>
              </div>
              <div className="h-1.5 bg-sand rounded">
                <div
                  className="h-1.5 bg-cedar rounded"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
